import { AxiosError, AxiosInstance } from 'axios';
import { Dispatch } from 'redux';
import { LOGOUT_ACTION, logOut } from '~/actions';
import type { TRootState } from '~/store/types';

/**
 * Account Standing Interceptor
 *
 * The API answers any request from a blocked or paused account with a 403 carrying one of
 * these messages. The person is signed out, and the signed-out screen shows the message.
 * Every other 403 (the Cloudflare challenge, office access errors) passes through untouched.
 */

const ACCOUNT_STANDING_MESSAGES = [
  'Your account is blocked. Please, reach out to support team',
  'Your account temporarily paused. Please, reach out to support team',
];

interface IAccountStandingStore {
  dispatch: Dispatch;
  getState: () => Pick<TRootState, 'auth' | 'loading'>;
}

export const attachAccountStanding = (api: AxiosInstance, store: IAccountStandingStore) => {
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ message?: string } | undefined>) => {
      const message = error.response?.data?.message;

      if (
        error.response?.status !== 403 ||
        !message ||
        !ACCOUNT_STANDING_MESSAGES.includes(message)
      ) {
        return Promise.reject(error);
      }

      // Requests that fail together each land here; sign out once. The sign-out marks itself
      // loading before this dispatch returns, and once it completes there is no token left.
      const { auth, loading } = store.getState();
      if (auth.accessToken && !loading[LOGOUT_ACTION]) {
        store.dispatch(logOut({ message }));
      }

      // Rejected without the response, so a saga's catch stores no error message and no
      // error popup opens beside the sign-out one.
      return Promise.reject(new Error(message));
    },
  );
};
